// Active Workout Page - Track workout in progress

import { query, run, saveDatabase } from '../db.js';
import { navigateTo, showToast, showConfirmDialog } from '../router.js';

let workoutState = {
  instanceId: null,
  templateId: null,
  completedSetIds: new Set(), // Track completed planned set IDs
  activeSetId: null, // Currently expanded set for input
  performedExercises: new Map(), // Map exercise_id to performed_exercise_id
  dynamicExercises: [], // Virtual exercise objects added mid-workout (not in template)
  additionalSets: new Map(), // Map workout_exercise_id to arrays of virtual copied sets
  startTime: null,
  elapsedSeconds: 0,
  timerInterval: null
};

export async function renderActiveWorkoutPage(params) {
  const templateId = params.id;
  const app = document.getElementById('app');

  const template = query('SELECT * FROM Template WHERE id = ?', [templateId])[0];

  if (!template) {
    showToast('Template not found', 'error');
    navigateTo('/templates');
    return;
  }

  const workoutExercises = query(`
    SELECT we.*, e.name, e.musclegroup, e.equipment, e.exercise_type, e.color
    FROM PlannedExercises we
    JOIN Exercises e ON we.exercise_id = e.id
    WHERE we.workout_id = ?
    ORDER BY we.id
  `, [templateId]);

  if (workoutExercises.length === 0) {
    showToast('No exercises in this template', 'error');
    navigateTo(`/templates/${templateId}`);
    return;
  }

  // Get planned sets for all exercises
  workoutExercises.forEach(we => {
    we.plannedSets = query(`
      SELECT * FROM PlannedSets WHERE workout_exercise_id = ? ORDER BY id
    `, [we.id]);

    // Merge in any additional copied sets from workoutState with deep copies
    const additionalSets = workoutState.additionalSets.get(we.id) || [];
    const additionalSetsCopies = additionalSets.map(set => ({
      id: set.id,
      workout_exercise_id: set.workout_exercise_id,
      weight: set.weight,
      reps: set.reps,
      duration_seconds: set.duration_seconds
    }));
    we.plannedSets = [...we.plannedSets, ...additionalSetsCopies];
  });

  // Merge dynamic exercises (added mid-workout) with template exercises
  if (workoutState.dynamicExercises && workoutState.dynamicExercises.length > 0) {
    // Create new objects with merged sets to avoid modifying workoutState
    const dynamicExercisesWithAdditionalSets = workoutState.dynamicExercises.map(de => {
      const additionalSets = workoutState.additionalSets.get(de.id) || [];
      // Create deep copies of additional sets to avoid shared references
      const additionalSetsCopies = additionalSets.map(set => ({
        id: set.id,
        workout_exercise_id: set.workout_exercise_id,
        weight: set.weight,
        reps: set.reps,
        duration_seconds: set.duration_seconds
      }));
      return {
        ...de,
        plannedSets: [...de.plannedSets, ...additionalSetsCopies]
      };
    });
    workoutExercises.push(...dynamicExercisesWithAdditionalSets);
  }

  // Initialize workout if not started
  if (!workoutState.instanceId || workoutState.templateId !== templateId) {
    const startTime = new Date();
    const instanceId = run(
      'INSERT INTO PerformedWorkout (template_id, start_time) VALUES (?, ?)',
      [templateId, formatTime(startTime)]
    );

    // Create PerformedExercises records for all template exercises
    const performedExercisesMap = new Map();
    workoutExercises.forEach(we => {
      const performedExerciseId = run(
        'INSERT INTO PerformedExercises (workout_instance_id, exercise_id, workout_exercise_id) VALUES (?, ?, ?)',
        [instanceId, we.exercise_id, we.id]
      );
      performedExercisesMap.set(we.exercise_id, performedExerciseId);
    });

    workoutState = {
      instanceId,
      templateId,
      completedSetIds: new Set(),
      activeSetId: null,
      performedExercises: performedExercisesMap,
      dynamicExercises: [],
      additionalSets: new Map(),
      startTime,
      elapsedSeconds: 0,
      timerInterval: null
    };

    await saveDatabase();
  }

  renderWorkoutUI(template, workoutExercises);
  startWorkoutTimer();
}

function renderWorkoutUI(template, workoutExercises) {
  const app = document.getElementById('app');

  const totalSets = workoutExercises.reduce((acc, we) => acc + we.plannedSets.length, 0);
  const completedCount = workoutState.completedSetIds.size;
  const progressPercent = totalSets > 0 ? (completedCount / totalSets) * 100 : 0;

  // Get current studio and previously used studios for autocomplete
  const currentWorkout = query('SELECT studio FROM PerformedWorkout WHERE id = ?', [workoutState.instanceId])[0];
  const currentStudio = currentWorkout?.studio || '';
  const previousStudios = query('SELECT DISTINCT studio FROM PerformedWorkout WHERE studio IS NOT NULL AND studio != "" ORDER BY id DESC LIMIT 10');

  app.innerHTML = `
    <div class="page safe-top" style="padding-bottom: 80px;">
      <!-- Header -->
      <div class="page-header" style="border-bottom: 1px solid var(--color-border);">
        <div class="page-header-top">
          <h1 class="page-title">${template.name}</h1>
          <div style="display: flex; align-items: center; gap: var(--spacing-sm); color: var(--color-primary);">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            <span class="stat-number" id="elapsed-time">00:00</span>
          </div>
        </div>

        <!-- Progress -->
        <div style="margin-top: var(--spacing-md);">
          <div style="display: flex; justify-content: space-between; margin-bottom: var(--spacing-xs);">
            <span class="text-sm text-muted">Progress</span>
            <span class="text-sm text-muted">${completedCount} / ${totalSets} sets</span>
          </div>
          <div class="progress-bar">
            <div class="progress-bar-fill" style="width: ${progressPercent}%"></div>
          </div>
        </div>

        <!-- Studio/Gym Name -->
        <div style="margin-top: var(--spacing-md);">
          <label style="display: block; margin-bottom: var(--spacing-xs); font-size: 0.875rem; color: var(--color-muted);">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align: middle; margin-right: 4px;">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
            Gym / Studio (optional)
          </label>
          <input
            type="text"
            id="studio-input"
            value="${currentStudio}"
            placeholder="e.g., LA Fitness, Home Gym..."
            list="studio-suggestions"
            style="font-size: 0.875rem;"
          />
          <datalist id="studio-suggestions">
            ${previousStudios.map(s => `<option value="${s.studio}">`).join('')}
          </datalist>
        </div>
      </div>

      <div style="padding: 0 var(--spacing-md);">
        <!-- All Exercises List -->
        <div style="margin: var(--spacing-lg) 0;">
          ${workoutExercises.map((exercise, exIdx) => renderExerciseWithSets(exercise, exIdx + 1)).join('')}
        </div>
      </div>

      <!-- Fixed Bottom Actions -->
      <div style="position: fixed; bottom: calc(70px + var(--safe-bottom)); left: 0; right: 0; padding: var(--spacing-md); background: var(--color-background); border-top: 1px solid var(--color-border);">
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-sm);">
          <button class="btn-secondary" id="add-exercise-floating-btn" style="padding: var(--spacing-md);">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add Exercise
          </button>
          <button class="btn-destructive" id="end-workout-btn" style="padding: var(--spacing-md);">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
            End Workout
          </button>
        </div>
      </div>
    </div>
  `;

  setupWorkoutEventListeners(workoutExercises);
}

function renderExerciseWithSets(exercise, exerciseNumber) {
  const exerciseType = exercise.exercise_type || 'weight_reps';
  const allSetsComplete = exercise.plannedSets.every(set =>
    workoutState.completedSetIds.has(set.id)
  );

  return `
    <div style="margin-bottom: var(--spacing-xl); ${allSetsComplete ? 'opacity: 0.6;' : ''}">
      <!-- Exercise Header -->
      <div style="display: flex; align-items: center; gap: var(--spacing-md); margin-bottom: var(--spacing-md);">
        <div style="width: 40px; height: 40px; border-radius: var(--radius-lg); background: ${allSetsComplete ? 'var(--color-success)' : 'linear-gradient(135deg, var(--color-primary) 0%, #a78bfa 100%)'}; display: flex; align-items: center; justify-content: center;">
          ${allSetsComplete ? `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          ` : `
            <span class="font-display" style="font-size: 1.25rem; font-weight: 700; color: white;">
              ${exerciseNumber}
            </span>
          `}
        </div>
        <div style="flex: 1;">
          <h3 class="font-display" style="font-size: 1.25rem; margin-bottom: 0.125rem;">
            ${exercise.name}
          </h3>
          <p class="text-xs text-muted" style="text-transform: capitalize;">
            ${exercise.musclegroup} · ${exercise.equipment}
          </p>
        </div>
      </div>

      <!-- All Sets for this Exercise -->
      <div style="display: flex; flex-direction: column; gap: var(--spacing-sm);">
        ${exercise.plannedSets.map((plannedSet, setIdx) => renderSingleSet(plannedSet, setIdx + 1, exercise, exerciseType)).join('')}

        ${renderCopySetButton(exercise, exerciseType)}
      </div>
    </div>
  `;
}

function renderCopySetButton(exercise, exerciseType) {
  // Only show if at least one set is completed
  const hasCompletedSets = exercise.plannedSets.some(set =>
    workoutState.completedSetIds.has(set.id)
  );

  if (!hasCompletedSets) {
    return '';
  }

  // Find the last completed set to determine what values to copy
  const lastCompletedSet = [...exercise.plannedSets]
    .reverse()
    .find(set => workoutState.completedSetIds.has(set.id));

  if (!lastCompletedSet) {
    return '';
  }

  // Get the actual performed values from the last completed set
  let performedSet = query(
    'SELECT * FROM PerformedSets WHERE planned_set_id = ? LIMIT 1',
    [lastCompletedSet.id]
  )[0];

  // Fallback for old data without planned_set_id
  if (!performedSet) {
    performedSet = query(
      'SELECT * FROM PerformedSets WHERE workout_instance_id = ? AND exercise_id = ? ORDER BY id DESC LIMIT 1',
      [workoutState.instanceId, exercise.exercise_id]
    )[0];
  }

  return `
    <button
      class="copy-set-btn"
      data-exercise-id="${exercise.exercise_id}"
      data-workout-exercise-id="${exercise.id}"
      data-exercise-type="${exerciseType}"
      data-copy-weight="${performedSet?.weight || lastCompletedSet.weight || ''}"
      data-copy-reps="${performedSet?.reps || lastCompletedSet.reps || ''}"
      data-copy-duration="${performedSet?.duration_seconds || lastCompletedSet.duration_seconds || ''}"
      style="width: 100%; padding: var(--spacing-md); background: var(--color-card); border: 2px dashed var(--color-primary); border-radius: var(--radius-lg); cursor: pointer; display: flex; align-items: center; justify-content: center; gap: var(--spacing-sm); color: var(--color-primary); font-weight: 500; margin-top: var(--spacing-sm); transition: all var(--transition-base);"
      title="Copy last set"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
      </svg>
      <span>Copy Last Set</span>
      <span class="text-sm text-muted">(${formatSetDisplay(
        performedSet?.weight || lastCompletedSet.weight,
        performedSet?.reps || lastCompletedSet.reps,
        performedSet?.duration_seconds || lastCompletedSet.duration_seconds,
        exerciseType
      )})</span>
    </button>
  `;
}

function renderSingleSet(plannedSet, setNumber, exercise, exerciseType) {
  const isCompleted = workoutState.completedSetIds.has(plannedSet.id);
  const isActive = workoutState.activeSetId === plannedSet.id;

  if (isCompleted) {
    // Show completed set with green border and checkmark
    // Query by planned_set_id to get the specific completed set
    let performedSet = query(
      'SELECT * FROM PerformedSets WHERE planned_set_id = ? LIMIT 1',
      [plannedSet.id]
    )[0];

    // Fallback for old data without planned_set_id
    if (!performedSet) {
      performedSet = query(
        'SELECT * FROM PerformedSets WHERE workout_instance_id = ? AND exercise_id = ? ORDER BY id DESC LIMIT 1',
        [workoutState.instanceId, exercise.exercise_id]
      )[0];
    }

    return `
      <div style="padding: var(--spacing-md); background: var(--color-success-light); border: 2px solid var(--color-success); border-radius: var(--radius-lg);">
        <div style="display: flex; align-items: center; justify-content: space-between;">
          <div style="display: flex; align-items: center; gap: var(--spacing-sm); flex: 1;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" stroke-width="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <span class="text-sm font-medium">Set ${setNumber}</span>
            <span class="stat-number text-sm">${formatSetDisplay(performedSet?.weight, performedSet?.reps, performedSet?.duration_seconds, exerciseType)}</span>
          </div>
          <div style="display: flex; gap: var(--spacing-xs);">
            <!-- Pencil Edit Button -->
            <button
              class="edit-completed-set-btn"
              data-performed-set-id="${performedSet?.id}"
              data-set-id="${plannedSet.id}"
              data-exercise-type="${exerciseType}"
              data-current-weight="${performedSet?.weight || ''}"
              data-current-reps="${performedSet?.reps || ''}"
              data-current-duration="${performedSet?.duration_seconds || ''}"
              style="padding: var(--spacing-xs); background: transparent; border: 1px solid var(--color-success); color: var(--color-success); border-radius: var(--radius-sm); cursor: pointer; display: flex; align-items: center; justify-content: center;"
              title="Edit set"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
            </button>
            <!-- Uncomplete Button -->
            <button
              class="uncomplete-set-btn"
              data-performed-set-id="${performedSet?.id}"
              data-set-id="${plannedSet.id}"
              style="padding: var(--spacing-xs); background: transparent; border: 1px solid var(--color-error); color: var(--color-error); border-radius: var(--radius-sm); cursor: pointer; display: flex; align-items: center; justify-content: center;"
              title="Uncomplete set"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  if (isActive) {
    // Show active set with input fields
    return renderSetInput(plannedSet, setNumber, exercise, exerciseType);
  }

  // Show pending set with pencil and checkmark icons
  return `
    <div style="padding: var(--spacing-md); background: var(--color-card); border: 2px solid var(--color-border); border-radius: var(--radius-lg); transition: all var(--transition-base);">
      <div style="display: flex; align-items: center; justify-content: space-between;">
        <div style="display: flex; align-items: center; gap: var(--spacing-sm); flex: 1;">
          <span class="text-sm font-medium">Set ${setNumber}</span>
          <span class="text-sm text-muted">Planned: ${formatSetDisplay(plannedSet.weight, plannedSet.reps, plannedSet.duration_seconds, exerciseType)}</span>
        </div>
        <div style="display: flex; gap: var(--spacing-xs);">
          <!-- Pencil Edit Button -->
          <button
            class="edit-pending-set-btn"
            data-set-id="${plannedSet.id}"
            data-exercise-id="${exercise.exercise_id}"
            data-exercise-type="${exerciseType}"
            data-planned-weight="${plannedSet.weight || ''}"
            data-planned-reps="${plannedSet.reps || ''}"
            data-planned-duration="${plannedSet.duration_seconds || ''}"
            style="padding: var(--spacing-xs); background: transparent; border: 1px solid var(--color-primary); color: var(--color-primary); border-radius: var(--radius-sm); cursor: pointer; display: flex; align-items: center; justify-content: center;"
            title="Edit set"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </button>
          <!-- Checkmark Complete Button -->
          <button
            class="inline-complete-btn"
            data-set-id="${plannedSet.id}"
            data-exercise-id="${exercise.exercise_id}"
            data-exercise-type="${exerciseType}"
            data-planned-weight="${plannedSet.weight || ''}"
            data-planned-reps="${plannedSet.reps || ''}"
            data-planned-duration="${plannedSet.duration_seconds || ''}"
            style="padding: var(--spacing-xs); background: var(--color-primary); color: white; border: none; border-radius: var(--radius-sm); cursor: pointer; display: flex; align-items: center; justify-content: center;"
            title="Complete set"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </button>
        </div>
      </div>
    </div>
  `;
}

function renderSetInput(plannedSet, setNumber, exercise, exerciseType) {
  // For time-based exercises, show time input
  if (exerciseType === 'time_based') {
    const defaultDuration = plannedSet.duration_seconds || 60;
    const minutes = Math.floor(defaultDuration / 60);
    const seconds = defaultDuration % 60;

    return `
      <div class="set-input" style="padding: var(--spacing-md); background: var(--color-card); border: 2px solid var(--color-primary); border-radius: var(--radius-lg);">
        <div class="set-input-header" style="margin-bottom: var(--spacing-md);">
          <span class="set-number-badge">Set ${setNumber}</span>
          <span class="text-sm text-muted">Planned: ${formatSetDuration(defaultDuration)}</span>
        </div>

        <div class="set-input-fields" style="margin-bottom: var(--spacing-md);">
          <div class="set-input-field">
            <label>Minutes</label>
            <input type="number" id="duration-minutes-${plannedSet.id}" value="${minutes}" min="0" max="60" />
          </div>
          <div class="set-input-field">
            <label>Seconds</label>
            <input type="number" id="duration-seconds-${plannedSet.id}" value="${seconds}" min="0" max="59" />
          </div>
        </div>

        <div class="set-input-actions">
          <button class="btn-primary" data-complete-set="${plannedSet.id}" data-exercise-id="${exercise.exercise_id}" data-exercise-type="${exerciseType}" style="width: 100%;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            Complete Set
          </button>
        </div>
      </div>
    `;
  }

  // For reps-based exercises (bodyweight or weighted)
  const defaultWeight = plannedSet.weight || 0;
  const defaultReps = plannedSet.reps || 8;
  const isRepsOnly = exerciseType === 'reps_only';

  return `
    <div class="set-input" style="padding: var(--spacing-md); background: var(--color-card); border: 2px solid var(--color-primary); border-radius: var(--radius-lg);">
      <div class="set-input-header" style="margin-bottom: var(--spacing-md);">
        <span class="set-number-badge">Set ${setNumber}</span>
        <span class="text-sm text-muted">Planned: ${formatSetDisplay(defaultWeight, defaultReps, null, exerciseType)}</span>
      </div>

      <div class="set-input-fields" style="margin-bottom: var(--spacing-md);">
        <div class="set-input-field">
          <label>Weight (kg)${isRepsOnly ? ' (optional)' : ''}</label>
          <input type="number" id="weight-input-${plannedSet.id}" value="${defaultWeight || ''}" step="0.5" min="0"
                 placeholder="${isRepsOnly ? 'Add weight if using' : '0'}" />
        </div>
        <div class="set-input-field">
          <label>Reps</label>
          <input type="number" id="reps-input-${plannedSet.id}" value="${defaultReps}" min="1" max="100" />
        </div>
      </div>

      <div class="set-input-actions">
        <button class="btn-primary" data-complete-set="${plannedSet.id}" data-exercise-id="${exercise.exercise_id}" data-exercise-type="${exerciseType}" style="width: 100%;">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          Complete Set
        </button>
      </div>
    </div>
  `;
}

function setupWorkoutEventListeners(workoutExercises) {
  const endBtn = document.getElementById('end-workout-btn');
  const studioInput = document.getElementById('studio-input');
  const addExerciseBtn = document.getElementById('add-exercise-btn');
  const addExerciseFloatingBtn = document.getElementById('add-exercise-floating-btn');

  endBtn?.addEventListener('click', () => handleEndWorkout());
  addExerciseBtn?.addEventListener('click', () => openAddExerciseModal(workoutExercises));
  addExerciseFloatingBtn?.addEventListener('click', () => openAddExerciseModal(workoutExercises));

  // Inline complete button handlers (checkmark on pending sets)
  document.querySelectorAll('.inline-complete-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const setId = parseInt(btn.getAttribute('data-set-id'));
      const exerciseId = parseInt(btn.getAttribute('data-exercise-id'));
      const exerciseType = btn.getAttribute('data-exercise-type');
      const plannedWeight = btn.getAttribute('data-planned-weight');
      const plannedReps = btn.getAttribute('data-planned-reps');
      const plannedDuration = btn.getAttribute('data-planned-duration');

      await handleQuickCompleteSet(setId, exerciseId, exerciseType, plannedWeight, plannedReps, plannedDuration);
    });
  });

  // Edit pending set button handlers (pencil on pending sets)
  document.querySelectorAll('.edit-pending-set-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const setId = parseInt(btn.getAttribute('data-set-id'));
      const exerciseId = parseInt(btn.getAttribute('data-exercise-id'));
      const exerciseType = btn.getAttribute('data-exercise-type');
      const plannedWeight = btn.getAttribute('data-planned-weight');
      const plannedReps = btn.getAttribute('data-planned-reps');
      const plannedDuration = btn.getAttribute('data-planned-duration');

      openEditSetModal(setId, exerciseId, exerciseType, true, {
        weight: plannedWeight,
        reps: plannedReps,
        duration: plannedDuration
      });
    });
  });

  // Edit completed set button handlers (pencil on completed sets)
  document.querySelectorAll('.edit-completed-set-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const performedSetId = parseInt(btn.getAttribute('data-performed-set-id'));
      const setId = parseInt(btn.getAttribute('data-set-id'));
      const exerciseType = btn.getAttribute('data-exercise-type');
      const currentWeight = btn.getAttribute('data-current-weight');
      const currentReps = btn.getAttribute('data-current-reps');
      const currentDuration = btn.getAttribute('data-current-duration');

      openEditSetModal(setId, null, exerciseType, false, {
        performedSetId: performedSetId,
        weight: currentWeight,
        reps: currentReps,
        duration: currentDuration
      });
    });
  });

  // Uncomplete set button handlers (X on completed sets)
  document.querySelectorAll('.uncomplete-set-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const performedSetId = parseInt(btn.getAttribute('data-performed-set-id'));
      const setId = parseInt(btn.getAttribute('data-set-id'));
      handleUncompleteSet(performedSetId, setId);
    });
  });

  // Complete set button handlers (from active set input form)
  document.querySelectorAll('[data-complete-set]').forEach(btn => {
    btn.addEventListener('click', () => {
      const setId = parseInt(btn.getAttribute('data-complete-set'));
      const exerciseId = parseInt(btn.getAttribute('data-exercise-id'));
      const exerciseType = btn.getAttribute('data-exercise-type');
      handleCompleteSet(setId, exerciseId, exerciseType);
    });
  });

  // Save studio name on change
  studioInput?.addEventListener('blur', async () => {
    const studio = studioInput.value.trim();
    try {
      run('UPDATE PerformedWorkout SET studio = ? WHERE id = ?', [studio || null, workoutState.instanceId]);
      await saveDatabase();
    } catch (err) {
      console.error('Failed to save studio name:', err);
    }
  });

  // Copy set button handlers
  document.querySelectorAll('.copy-set-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const exerciseId = parseInt(btn.getAttribute('data-exercise-id'));
      const workoutExerciseId = parseInt(btn.getAttribute('data-workout-exercise-id'));
      const exerciseType = btn.getAttribute('data-exercise-type');
      const copyWeight = btn.getAttribute('data-copy-weight');
      const copyReps = btn.getAttribute('data-copy-reps');
      const copyDuration = btn.getAttribute('data-copy-duration');

      handleCopySet(exerciseId, workoutExerciseId, exerciseType, {
        weight: copyWeight,
        reps: copyReps,
        duration: copyDuration
      });
    });
  });
}

function handleCopySet(exerciseId, workoutExerciseId, exerciseType, copyValues) {
  // Create a virtual planned set with negative ID (to avoid conflicts with real IDs)
  const virtualSetId = -(Date.now());

  const newSet = {
    id: virtualSetId,
    workout_exercise_id: workoutExerciseId,
    weight: exerciseType === 'time_based' ? null : (parseFloat(copyValues.weight) || null),
    reps: exerciseType === 'time_based' ? null : (parseInt(copyValues.reps) || 8),
    duration_seconds: exerciseType === 'time_based' ? (parseInt(copyValues.duration) || 60) : null
  };

  // Get existing additional sets for this exercise or create new array
  const existingSets = workoutState.additionalSets.get(workoutExerciseId) || [];
  existingSets.push(newSet);
  workoutState.additionalSets.set(workoutExerciseId, existingSets);

  showToast('Set copied! Ready to edit and complete.', 'success');

  // Re-render to show the new pending set
  renderActiveWorkoutPage({ id: workoutState.templateId });
}

async function handleCompleteSet(setId, exerciseId, exerciseType) {
  // Get performed_exercise_id from the map
  const performedExerciseId = workoutState.performedExercises.get(exerciseId);

  if (exerciseType === 'time_based') {
    // Handle time-based exercise
    const minutesInput = document.getElementById(`duration-minutes-${setId}`);
    const secondsInput = document.getElementById(`duration-seconds-${setId}`);
    const totalSeconds = (parseInt(minutesInput?.value) || 0) * 60 + (parseInt(secondsInput?.value) || 0);

    if (totalSeconds < 1) {
      showToast('Please enter a duration', 'error');
      return;
    }

    // Save to database with planned_set_id and performed_exercise_id
    // Use setId for both positive (database) and negative (virtual) set IDs
    run(
      'INSERT INTO PerformedSets (workout_instance_id, exercise_id, planned_set_id, performed_exercise_id, set_number, duration_seconds) VALUES (?, ?, ?, ?, ?, ?)',
      [workoutState.instanceId, exerciseId, setId, performedExerciseId, workoutState.completedSetIds.size + 1, totalSeconds]
    );
  } else {
    // Handle weight/reps based exercise
    const weightInput = document.getElementById(`weight-input-${setId}`);
    const repsInput = document.getElementById(`reps-input-${setId}`);

    let weight = null;
    if (weightInput && weightInput.value) {
      const weightValue = parseFloat(weightInput.value);
      if (weightValue && weightValue > 0) {
        weight = weightValue;
      }
    }

    const reps = parseInt(repsInput?.value) || 0;

    if (reps < 1) {
      showToast('Please enter at least 1 rep', 'error');
      return;
    }

    // Save to database with planned_set_id and performed_exercise_id
    // Use setId for both positive (database) and negative (virtual) set IDs
    run(
      'INSERT INTO PerformedSets (workout_instance_id, exercise_id, planned_set_id, performed_exercise_id, set_number, weight, reps) VALUES (?, ?, ?, ?, ?, ?, ?)',
      [workoutState.instanceId, exerciseId, setId, performedExerciseId, workoutState.completedSetIds.size + 1, weight, reps]
    );
  }

  // Mark set as completed
  workoutState.completedSetIds.add(setId);
  workoutState.activeSetId = null;

  await saveDatabase();

  // Re-render
  renderActiveWorkoutPage({ id: workoutState.templateId });
}

async function handleQuickCompleteSet(setId, exerciseId, exerciseType, plannedWeight, plannedReps, plannedDuration) {
  // Get performed_exercise_id from the map
  const performedExerciseId = workoutState.performedExercises.get(exerciseId);

  try {
    if (exerciseType === 'time_based') {
      const totalSeconds = parseInt(plannedDuration) || 0;

      if (totalSeconds < 1) {
        showToast('Invalid duration', 'error');
        return;
      }

      // Save to database with planned_set_id and performed_exercise_id
      // Use setId for both positive (database) and negative (virtual) set IDs
      run(
        'INSERT INTO PerformedSets (workout_instance_id, exercise_id, planned_set_id, performed_exercise_id, set_number, duration_seconds) VALUES (?, ?, ?, ?, ?, ?)',
        [workoutState.instanceId, exerciseId, setId, performedExerciseId, workoutState.completedSetIds.size + 1, totalSeconds]
      );
    } else {
      // Handle weight/reps based exercise
      const weight = parseFloat(plannedWeight) || null;
      const reps = parseInt(plannedReps) || null;

      if (!reps || reps < 1) {
        showToast('Invalid reps', 'error');
        return;
      }

      // Save to database with planned_set_id and performed_exercise_id
      // Use setId for both positive (database) and negative (virtual) set IDs
      run(
        'INSERT INTO PerformedSets (workout_instance_id, exercise_id, planned_set_id, performed_exercise_id, set_number, weight, reps) VALUES (?, ?, ?, ?, ?, ?, ?)',
        [workoutState.instanceId, exerciseId, setId, performedExerciseId, workoutState.completedSetIds.size + 1, weight, reps]
      );
    }

    // Mark set as completed
    workoutState.completedSetIds.add(setId);

    await saveDatabase();
    showToast('Set completed!', 'success');

    // Re-render
    renderActiveWorkoutPage({ id: workoutState.templateId });
  } catch (err) {
    console.error('Failed to complete set:', err);
    showToast('Failed to complete set', 'error');
  }
}

function openEditSetModal(setId, exerciseId, exerciseType, isPending, currentValues) {
  const modal = document.createElement('div');
  modal.className = 'modal-overlay';
  modal.style.display = 'flex';

  // Build modal content based on exercise type
  let inputFields = '';

  if (exerciseType === 'time_based') {
    const duration = parseInt(currentValues.duration) || 0;
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;

    inputFields = `
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-md); margin-bottom: var(--spacing-lg);">
        <div>
          <label style="display: block; margin-bottom: var(--spacing-xs); font-weight: 500; font-size: 0.875rem;">Minutes</label>
          <input
            type="number"
            id="edit-duration-minutes"
            value="${minutes}"
            min="0"
            max="60"
            style="width: 100%; font-size: 1.5rem; text-align: center; padding: var(--spacing-md); border: 2px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-card);"
          />
        </div>
        <div>
          <label style="display: block; margin-bottom: var(--spacing-xs); font-weight: 500; font-size: 0.875rem;">Seconds</label>
          <input
            type="number"
            id="edit-duration-seconds"
            value="${seconds}"
            min="0"
            max="59"
            style="width: 100%; font-size: 1.5rem; text-align: center; padding: var(--spacing-md); border: 2px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-card);"
          />
        </div>
      </div>
    `;
  } else {
    inputFields = `
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-md); margin-bottom: var(--spacing-lg);">
        <div>
          <label style="display: block; margin-bottom: var(--spacing-xs); font-weight: 500; font-size: 0.875rem;">Weight (kg)</label>
          <input
            type="number"
            id="edit-weight"
            value="${currentValues.weight || ''}"
            step="0.5"
            min="0"
            placeholder="0"
            style="width: 100%; font-size: 1.5rem; text-align: center; padding: var(--spacing-md); border: 2px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-card);"
          />
        </div>
        <div>
          <label style="display: block; margin-bottom: var(--spacing-xs); font-weight: 500; font-size: 0.875rem;">Reps</label>
          <input
            type="number"
            id="edit-reps"
            value="${currentValues.reps || ''}"
            min="1"
            max="100"
            style="width: 100%; font-size: 1.5rem; text-align: center; padding: var(--spacing-md); border: 2px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-card);"
          />
        </div>
      </div>
    `;
  }

  modal.innerHTML = `
    <div class="modal" style="max-width: 400px;">
      <div class="modal-header">
        <h2 class="modal-title">Edit Set</h2>
        <button class="modal-close" id="close-edit-modal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <div class="modal-body">
        ${inputFields}
      </div>
      <div class="modal-footer">
        <button class="btn-ghost" id="cancel-edit-set">Cancel</button>
        <button class="btn-primary" id="confirm-edit-set">Save</button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  const closeModal = () => {
    modal.remove();
  };

  modal.querySelector('#close-edit-modal').addEventListener('click', closeModal);
  modal.querySelector('#cancel-edit-set').addEventListener('click', closeModal);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });

  modal.querySelector('#confirm-edit-set').addEventListener('click', async () => {
    try {
      if (exerciseType === 'time_based') {
        const minutes = parseInt(document.getElementById('edit-duration-minutes').value) || 0;
        const seconds = parseInt(document.getElementById('edit-duration-seconds').value) || 0;
        const totalSeconds = minutes * 60 + seconds;

        if (totalSeconds < 1) {
          showToast('Please enter a valid duration', 'error');
          return;
        }

        if (isPending) {
          // For pending sets, complete the set with the edited values
          // Use setId for both positive (database) and negative (virtual) set IDs
          const performedExerciseId = workoutState.performedExercises.get(exerciseId);
          run(
            'INSERT INTO PerformedSets (workout_instance_id, exercise_id, planned_set_id, performed_exercise_id, set_number, duration_seconds) VALUES (?, ?, ?, ?, ?, ?)',
            [workoutState.instanceId, exerciseId, setId, performedExerciseId, workoutState.completedSetIds.size + 1, totalSeconds]
          );
          workoutState.completedSetIds.add(setId);
        } else {
          // For completed sets, update the existing record
          run(
            'UPDATE PerformedSets SET duration_seconds = ? WHERE id = ?',
            [totalSeconds, currentValues.performedSetId]
          );
        }
      } else {
        const weight = parseFloat(document.getElementById('edit-weight').value) || null;
        const reps = parseInt(document.getElementById('edit-reps').value) || 0;

        if (reps < 1) {
          showToast('Please enter at least 1 rep', 'error');
          return;
        }

        if (isPending) {
          // For pending sets, complete the set with the edited values
          // Use setId for both positive (database) and negative (virtual) set IDs
          const performedExerciseId = workoutState.performedExercises.get(exerciseId);
          run(
            'INSERT INTO PerformedSets (workout_instance_id, exercise_id, planned_set_id, performed_exercise_id, set_number, weight, reps) VALUES (?, ?, ?, ?, ?, ?, ?)',
            [workoutState.instanceId, exerciseId, setId, performedExerciseId, workoutState.completedSetIds.size + 1, weight, reps]
          );
          workoutState.completedSetIds.add(setId);
        } else {
          // For completed sets, update the existing record
          run(
            'UPDATE PerformedSets SET weight = ?, reps = ? WHERE id = ?',
            [weight, reps, currentValues.performedSetId]
          );
        }
      }

      await saveDatabase();
      closeModal();
      showToast(isPending ? 'Set completed' : 'Set updated', 'success');
      renderActiveWorkoutPage({ id: workoutState.templateId });
    } catch (err) {
      console.error('Failed to save set:', err);
      showToast('Failed to save set', 'error');
    }
  });
}

async function handleUncompleteSet(performedSetId, setId) {
  const confirmed = await showConfirmDialog(
    "Remove this completed set? You can complete it again.",
    "Uncomplete"
  );

  if (!confirmed) return;

  try {
    // Delete the PerformedSets record
    run('DELETE FROM PerformedSets WHERE id = ?', [performedSetId]);

    // Remove from completedSetIds in state
    workoutState.completedSetIds.delete(setId);

    await saveDatabase();
    showToast('Set uncompleted', 'info');

    // Re-render to show pending state
    renderActiveWorkoutPage({ id: workoutState.templateId });
  } catch (err) {
    console.error('Failed to uncomplete set:', err);
    showToast('Failed to uncomplete set', 'error');
  }
}

async function handleEndWorkout() {
  // Get current studio value
  const currentWorkout = query('SELECT studio FROM PerformedWorkout WHERE id = ?', [workoutState.instanceId])[0];
  const currentStudio = currentWorkout?.studio || '';
  const previousStudios = query('SELECT DISTINCT studio FROM PerformedWorkout WHERE studio IS NOT NULL AND studio != "" ORDER BY id DESC LIMIT 10');

  return new Promise((resolve) => {
    const modal = document.createElement('div');
    modal.className = 'modal-overlay';
    modal.style.display = 'flex';
    modal.innerHTML = `
      <div class="modal" style="max-width: 400px;">
        <div class="modal-header">
          <h2 class="modal-title">End Workout</h2>
        </div>
        <div class="modal-body">
          <p style="margin-bottom: var(--spacing-md);">Save your workout progress?</p>

          <div style="margin-bottom: var(--spacing-md);">
            <label style="display: block; margin-bottom: var(--spacing-xs); font-weight: 500;">
              Gym / Studio (optional)
            </label>
            <input
              type="text"
              id="end-workout-studio"
              value="${currentStudio}"
              placeholder="e.g., LA Fitness, Home Gym..."
              list="end-workout-studio-suggestions"
            />
            <datalist id="end-workout-studio-suggestions">
              ${previousStudios.map(s => `<option value="${s.studio}">`).join('')}
            </datalist>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" id="cancel-end-workout">Cancel</button>
          <button class="btn-primary" id="confirm-end-workout">End Workout</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    const closeModal = () => {
      modal.remove();
      resolve(false);
    };

    modal.querySelector('#cancel-end-workout').addEventListener('click', closeModal);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    modal.querySelector('#confirm-end-workout').addEventListener('click', async () => {
      const studioInput = document.getElementById('end-workout-studio');
      const studio = studioInput.value.trim();

      // Update studio before finishing
      try {
        run('UPDATE PerformedWorkout SET studio = ? WHERE id = ?', [studio || null, workoutState.instanceId]);
        await saveDatabase();
      } catch (err) {
        console.error('Failed to save studio name:', err);
      }

      modal.remove();
      await finishWorkout();
      resolve(true);
    });
  });
}

async function finishWorkout() {
  const endTime = new Date();

  // Update workout instance
  run(
    'UPDATE PerformedWorkout SET end_time = ? WHERE id = ?',
    [formatTime(endTime), workoutState.instanceId]
  );

  await saveDatabase();

  // Clear timer
  if (workoutState.timerInterval) {
    clearInterval(workoutState.timerInterval);
  }

  // Reset state
  workoutState = {
    instanceId: null,
    templateId: null,
    completedSetIds: new Set(),
    activeSetId: null,
    performedExercises: new Map(),
    dynamicExercises: [],
    additionalSets: new Map(),
    startTime: null,
    elapsedSeconds: 0,
    timerInterval: null
  };

  showToast('Workout completed!', 'success');
  navigateTo('/');
}

function openAddExerciseModal(workoutExercises) {
  // Fetch all exercises
  const allExercises = query('SELECT * FROM Exercises ORDER BY musclegroup, name');
  const muscleGroups = ['chest', 'back', 'shoulders', 'legs', 'biceps', 'triceps', 'core', 'cardio'];

  const MUSCLE_GROUP_COLORS = {
    chest: '#FF6B6B',
    back: '#4ECDC4',
    shoulders: '#95E1D3',
    legs: '#F38181',
    triceps: '#AA96DA',
    biceps: '#FCBAD3',
    core: '#FFFFD2',
    cardio: '#FF9500'
  };

  // Create modal
  const modal = document.createElement('div');
  modal.className = 'modal-overlay';
  modal.style.display = 'flex';
  modal.innerHTML = `
    <div class="modal" style="max-width: 600px; max-height: 90vh; display: flex; flex-direction: column;">
      <div class="modal-header">
        <h2 class="modal-title">Add Exercise</h2>
        <button class="modal-close" id="close-add-exercise-modal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <div class="modal-body" style="flex: 1; overflow: hidden; display: flex; flex-direction: column;">
        <!-- Search -->
        <input
          type="text"
          id="exercise-modal-search"
          placeholder="Search exercises..."
          style="margin-bottom: var(--spacing-md);"
        />

        <!-- Muscle Group Filter -->
        <div style="display: flex; gap: var(--spacing-xs); overflow-x: auto; padding-bottom: var(--spacing-sm); margin-bottom: var(--spacing-md); position: relative; z-index: 10; background: var(--color-card); padding-top: var(--spacing-xs); flex-shrink: 0; min-height: 50px;">
          <button class="filter-chip active" data-filter="all">All</button>
          ${muscleGroups.map(mg => `
            <button class="filter-chip" data-filter="${mg}" style="text-transform: capitalize;">
              <div style="width: 12px; height: 12px; background-color: ${MUSCLE_GROUP_COLORS[mg]}; border-radius: 2px; display: inline-block; margin-right: 6px;"></div>
              ${mg}
            </button>
          `).join('')}
        </div>

        <!-- Exercise List -->
        <div id="exercise-modal-list" style="flex: 1; overflow-y: auto;">
          ${renderModalExerciseList(allExercises, MUSCLE_GROUP_COLORS)}
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  // Setup modal event listeners
  const closeModal = () => {
    modal.remove();
  };

  modal.querySelector('#close-add-exercise-modal').addEventListener('click', closeModal);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });

  // Search functionality
  const searchInput = modal.querySelector('#exercise-modal-search');
  const listContainer = modal.querySelector('#exercise-modal-list');

  searchInput.addEventListener('input', () => {
    const searchTerm = searchInput.value.toLowerCase();
    const activeFilter = modal.querySelector('.filter-chip.active')?.getAttribute('data-filter') || 'all';

    let filtered = allExercises;

    if (searchTerm) {
      filtered = filtered.filter(ex =>
        ex.name.toLowerCase().includes(searchTerm) ||
        ex.musclegroup.toLowerCase().includes(searchTerm) ||
        ex.equipment.toLowerCase().includes(searchTerm)
      );
    }

    if (activeFilter !== 'all') {
      filtered = filtered.filter(ex => ex.musclegroup === activeFilter);
    }

    listContainer.innerHTML = renderModalExerciseList(filtered, MUSCLE_GROUP_COLORS);
    setupModalExerciseClickHandlers(filtered, workoutExercises, closeModal);
  });

  // Filter functionality
  modal.querySelectorAll('.filter-chip').forEach(chip => {
    chip.addEventListener('click', () => {
      modal.querySelectorAll('.filter-chip').forEach(c => c.classList.remove('active'));
      chip.classList.add('active');

      const filter = chip.getAttribute('data-filter');
      const searchTerm = searchInput.value.toLowerCase() || '';

      let filtered = allExercises;

      if (searchTerm) {
        filtered = filtered.filter(ex =>
          ex.name.toLowerCase().includes(searchTerm) ||
          ex.musclegroup.toLowerCase().includes(searchTerm) ||
          ex.equipment.toLowerCase().includes(searchTerm)
        );
      }

      if (filter !== 'all') {
        filtered = filtered.filter(ex => ex.musclegroup === filter);
      }

      listContainer.innerHTML = renderModalExerciseList(filtered, MUSCLE_GROUP_COLORS);
      setupModalExerciseClickHandlers(filtered, workoutExercises, closeModal);
    });
  });

  // Setup click handlers for exercise selection
  setupModalExerciseClickHandlers(allExercises, workoutExercises, closeModal);
}

function renderModalExerciseList(exercises, MUSCLE_GROUP_COLORS) {
  if (exercises.length === 0) {
    return `
      <div class="empty-state">
        <p class="text-muted">No exercises found</p>
      </div>
    `;
  }

  return exercises.map(ex => {
    const color = ex.color || MUSCLE_GROUP_COLORS[ex.musclegroup] || '#808080';
    return `
      <div class="exercise-card" data-exercise-id="${ex.id}" style="margin-bottom: var(--spacing-sm); cursor: pointer;">
        <div class="exercise-card-header">
          <div style="display: flex; align-items: center; gap: var(--spacing-sm); flex: 1;">
            <div style="width: 20px; height: 20px; background-color: ${color}; border-radius: 4px; flex-shrink: 0;"></div>
            <div style="flex: 1;">
              <div class="exercise-name">${ex.name}</div>
              <div class="exercise-meta">${ex.equipment} • ${ex.musclegroup}</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }).join('');
}

function setupModalExerciseClickHandlers(exercises, workoutExercises, closeModal) {
  document.querySelectorAll('#exercise-modal-list .exercise-card').forEach(card => {
    card.addEventListener('click', () => {
      const exerciseId = parseInt(card.getAttribute('data-exercise-id'));
      const exercise = exercises.find(ex => ex.id === exerciseId);
      if (exercise) {
        openSetConfigModal(exercise, workoutExercises, closeModal);
      }
    });
  });
}

function openSetConfigModal(exercise, workoutExercises, closeExerciseModal) {
  const modal = document.createElement('div');
  modal.className = 'modal-overlay';
  modal.style.display = 'flex';
  modal.innerHTML = `
    <div class="modal" style="max-width: 400px;">
      <div class="modal-header">
        <h2 class="modal-title">Configure Sets</h2>
        <button class="modal-close" id="close-set-config-modal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <div class="modal-body">
        <p style="margin-bottom: var(--spacing-md);">
          Adding: <strong>${exercise.name}</strong>
        </p>

        <div style="margin-bottom: var(--spacing-md);">
          <label style="display: block; margin-bottom: var(--spacing-xs); font-weight: 500;">
            Number of Sets
          </label>
          <input
            type="number"
            id="num-sets-input"
            value="3"
            min="1"
            max="10"
            style="width: 100%;"
          />
          <p class="text-xs text-muted" style="margin-top: 0.25rem;">
            How many sets do you want to add? (1-10)
          </p>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn-ghost" id="cancel-set-config">Cancel</button>
        <button class="btn-primary" id="confirm-add-exercise">Add to Workout</button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  const closeModal = () => {
    modal.remove();
  };

  modal.querySelector('#close-set-config-modal').addEventListener('click', closeModal);
  modal.querySelector('#cancel-set-config').addEventListener('click', closeModal);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });

  modal.querySelector('#confirm-add-exercise').addEventListener('click', async () => {
    const numSets = parseInt(modal.querySelector('#num-sets-input').value) || 3;

    if (numSets < 1 || numSets > 10) {
      showToast('Please enter 1-10 sets', 'error');
      return;
    }

    try {
      await addExerciseToActiveWorkout(exercise, numSets, workoutExercises);
      closeModal();
      closeExerciseModal();
      showToast(`Added ${exercise.name} with ${numSets} sets`, 'success');
    } catch (err) {
      console.error('Failed to add exercise:', err);
      showToast('Failed to add exercise', 'error');
    }
  });
}

async function addExerciseToActiveWorkout(exercise, numSets, workoutExercises) {
  // Create PerformedExercises record for this dynamic exercise (NULL workout_exercise_id)
  const performedExerciseId = run(
    'INSERT INTO PerformedExercises (workout_instance_id, exercise_id, workout_exercise_id) VALUES (?, ?, NULL)',
    [workoutState.instanceId, exercise.id]
  );

  // Store in performedExercises map
  workoutState.performedExercises.set(exercise.id, performedExerciseId);

  // Generate temporary negative ID for virtual exercise (avoid conflicts with real IDs)
  const virtualExerciseId = -(Date.now());

  // Create virtual planned sets (in-memory only, not in database)
  const exerciseType = exercise.exercise_type || 'weight_reps';
  const virtualPlannedSets = [];

  for (let i = 0; i < numSets; i++) {
    const virtualSetId = -(Date.now() + i); // Unique negative ID for each set

    if (exerciseType === 'time_based') {
      virtualPlannedSets.push({
        id: virtualSetId,
        workout_exercise_id: virtualExerciseId,
        duration_seconds: 60, // Default 60 seconds
        weight: null,
        reps: null
      });
    } else {
      virtualPlannedSets.push({
        id: virtualSetId,
        workout_exercise_id: virtualExerciseId,
        weight: 0, // Default 0kg
        reps: 8, // Default 8 reps
        duration_seconds: null
      });
    }
  }

  // Create virtual exercise object (mimics structure from PlannedExercises JOIN Exercises query)
  const virtualExercise = {
    id: virtualExerciseId,
    workout_id: workoutState.templateId,
    exercise_id: exercise.id,
    name: exercise.name,
    musclegroup: exercise.musclegroup,
    equipment: exercise.equipment,
    exercise_type: exerciseType,
    color: exercise.color || '#808080',
    plannedSets: virtualPlannedSets
  };

  // Add to dynamicExercises array in workoutState
  workoutState.dynamicExercises.push(virtualExercise);

  await saveDatabase();

  // Re-render the workout page with updated exercises
  renderActiveWorkoutPage({ id: workoutState.templateId });
}

function startWorkoutTimer() {
  if (workoutState.timerInterval) {
    clearInterval(workoutState.timerInterval);
  }

  workoutState.timerInterval = setInterval(() => {
    workoutState.elapsedSeconds = Math.floor((Date.now() - workoutState.startTime.getTime()) / 1000);
    const display = document.getElementById('elapsed-time');
    if (display) {
      display.textContent = formatDuration(workoutState.elapsedSeconds);
    }
  }, 1000);
}

function formatTime(date) {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

// Helper functions for exercise sets display
function formatSetDisplay(weight, reps, duration, exerciseType) {
  if (exerciseType === 'time_based') {
    return formatSetDuration(duration);
  }

  if (!weight || weight === 0) {
    return `${reps} reps`;
  }

  return `${weight} kg × ${reps}`;
}

function formatSetDuration(seconds) {
  if (!seconds) return '0s';
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}
